import { Injectable } from "@angular/core"
import { HttpErrorResponse } from "@angular/common/http"
import { context, Context, metrics, Span, SpanKind, SpanStatusCode, trace } from "@opentelemetry/api"
import { logs, SeverityNumber } from "@opentelemetry/api-logs"
import { sanitizeRoute } from "./observability.config"

export interface OperationHandle {
  otelContext: Context
  succeed(): void
  fail(error: unknown): void
}

@Injectable({ providedIn: "root" })
export class ObservabilityService {
  private readonly tracer = trace.getTracer("lex-hub-front-end")
  private readonly meter = metrics.getMeter("lex-hub-front-end")
  private readonly logger = logs.getLogger("lex-hub-front-end")
  private readonly operationCount = this.meter.createCounter("app.operation.count", { unit: "1" })
  private readonly operationDuration = this.meter.createHistogram("app.operation.duration", { unit: "s" })
  private readonly httpDuration = this.meter.createHistogram("http.client.request.duration", { unit: "s" })

  startOperation(operationName: string, initialAttributes: Record<string, string> = {}): OperationHandle {
    const startedAt = performance.now()
    const span = this.tracer.startSpan(`app.${operationName}`, {
      kind: SpanKind.INTERNAL,
      attributes: { "app.operation.name": operationName, ...initialAttributes },
    })
    const otelContext = trace.setSpan(context.active(), span)

    return {
      otelContext,
      succeed: () => this.finishOperation(span, operationName, startedAt, "success"),
      fail: (error: unknown) => this.finishOperation(span, operationName, startedAt, "error", error),
    }
  }

  trackNavigation(url: string): void {
    const route = sanitizeRoute(url)
    const operation = this.startOperation("page.navigation", { "app.page.route": route })
    operation.succeed()
  }

  trackHttp(span: Span, method: string, serverAddress: string, statusCode?: number, error?: unknown, startedAt?: number): void {
    const result = error || (statusCode && statusCode >= 400) ? "error" : "success"
    const attributes: Record<string, string | number> = {
      "http.request.method": method,
      "server.address": serverAddress,
    }

    if (statusCode !== undefined) {
      attributes["http.response.status_code"] = statusCode
    }
    if (result === "error") {
      attributes["error.type"] = this.errorType(error)
      span.setStatus({ code: SpanStatusCode.ERROR })
    }

    span.setAttributes(attributes)
    span.end()
    this.httpDuration.record((performance.now() - (startedAt || performance.now())) / 1000, attributes)
  }

  trackUnhandledError(error: unknown): void {
    const operation = this.startOperation("application.error")
    operation.fail(error)
  }

  private finishOperation(span: Span, operationName: string, startedAt: number, result: "success" | "error", error?: unknown): void {
    const attributes: Record<string, string> = {
      "app.operation.name": operationName,
      "app.operation.result": result,
    }

    if (error) {
      attributes["error.type"] = this.errorType(error)
      span.setStatus({ code: SpanStatusCode.ERROR })
    }

    span.setAttributes(attributes)
    span.end()
    const duration = (performance.now() - startedAt) / 1000
    this.operationCount.add(1, attributes)
    this.operationDuration.record(duration, attributes)
    this.logger.emit({
      severityNumber: result === "error" ? SeverityNumber.ERROR : SeverityNumber.INFO,
      severityText: result === "error" ? "ERROR" : "INFO",
      body: result === "error" ? "Application operation failed" : "Application operation completed",
      attributes,
      context: trace.setSpan(context.active(), span),
    })
  }

  private errorType(error: unknown): string {
    if (error instanceof HttpErrorResponse) return "HttpErrorResponse"
    if (error instanceof Error && error.name) return error.name.slice(0, 80)
    return "UnknownError"
  }
}
